import './../../Player.css';
import React, {useContext, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerContext } from '../../PlayerContext';
import { browserLocales, weekdayTextToGroupedByDaysOpeningHours } from '../../Utils';
import FadeInPanel from '../components/FadeInPanel';
import ColorContrastChecker from 'color-contrast-checker';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { hasChainRestaurants } from '../../chainRestaurantUtils';

function HoursScreen(props){
    const [isOpened, setIsOpened] = useState(false);

    const storyDimensions = useContext(PlayerContext).get("storyDimensions");
    const propagateFocusChange = useContext(PlayerContext).get("propagateFocusChange");
    const setActiveFeature = useContext(PlayerContext).get("setActiveFeature");
    const activeFeature = useContext(PlayerContext).get("activeFeature");
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const fromDemo = useContext(PlayerContext).get("fromDemo");
    const [showTexts, setShowTexts] = useState(true);
    const idPrefix = fromDemo ? (fromDemo +"-") : "";

    const hoursPanelRootStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: storyDimensions.containerWidth,
        height: storyDimensions.containerHeight,
        pointerEvents: 'auto',
        zIndex: 20,
        overflow: 'hidden',
    };

    const hoursOverlayStyle = {
        width: storyDimensions.containerWidth,
        height: storyDimensions.containerHeight,
        background: '#FFFFFFD0',
        pointerEvents: 'auto',
    };

    const hoursScrollAreaStyle = {
        position: 'relative',
        zIndex: 20,
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        WebkitOverflowScrolling: 'touch',
    };

    function renderHoursBackdrop() {
        return (
            <div
                id={idPrefix + 'gallery-overlay'}
                className="BlockingOverlay"
                style={hoursOverlayStyle}
                onClick={close}
            />
        );
    }

    const { t } = useTranslation();    

    if (props.story.posId == null && !hasChainRestaurants(props.story)) return (null);

    function cutMultipleHours(hourText){
        const language = browserLocales(true)[0] === "fr" ? "fr" : "en";
        if (language === "fr"){
            return hourText;
        }
        else {
            const splittedLine = hourText.split(",");         
            return splittedLine.map((line, index)=>{            
                let carriageReturn = false;
                if (splittedLine.indexOf(line) > 0){
                    carriageReturn = true;
                }
                return <React.Fragment key={`hour-line-${index}`}>{carriageReturn && <br/>}{line.trim()}</React.Fragment>;
            });
        }
    }

    const translateWeekdayDescriptions = (weekdayDescriptions) => {
        const dayMap = {
            'lundi': 'Monday',
            'mardi': 'Tuesday',
            'mercredi': 'Wednesday',
            'jeudi': 'Thursday',
            'vendredi': 'Friday',
            'samedi': 'Saturday',
            'dimanche': 'Sunday',
            'Fermé': 'Closed'
        };

        const translateHours = (hours) => {
            if (hours === 'Fermé') return 'Closed';
            
            // Gérer les périodes multiples (ex: "09:00 - 14:30, 17:00 - 22:00")
            const periods = hours.split(', ');
            return periods.map(period => {
                const [start, end] = period.split(' - ');
                const [startHour, startMinute] = start.split(':');
                const [endHour, endMinute] = end.split(':');
                
                const startHourNum = parseInt(startHour);
                const endHourNum = parseInt(endHour);
                
                const startPeriod = startHourNum >= 12 ? 'PM' : 'AM';
                const endPeriod = endHourNum >= 12 ? 'PM' : 'AM';
                
                const startHour12 = startHourNum % 12 || 12;
                const endHour12 = endHourNum % 12 || 12;
                
                // Si les deux heures sont dans la même période (AM ou PM), on n'affiche qu'une seule fois
                if (startPeriod === endPeriod) {
                    return `${startHour12}:${startMinute} - ${endHour12}:${endMinute} ${endPeriod}`;
                }
                return `${startHour12}:${startMinute} ${startPeriod} - ${endHour12}:${endMinute} ${endPeriod}`;
            }).join(', ');
        };

        return weekdayDescriptions.map(description => {
            const [day, hours] = description.split(': ');
            const translatedDay = dayMap[day.toLowerCase()] || day;
            const translatedHours = translateHours(hours);
            return `${translatedDay}: ${translatedHours}`;
        });
    };    

      const close = () => {        
        propagateFocusChange(true);
        setIsOpened(false);        
        setActiveFeature(null);
      };       
      
      function showCloseButton(){
        return (
            <div style={{
                position: 'absolute',
                bottom: 16 * sizeMultiplier,
                left: 0,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 40
            }}>
                
                <IconButton
                    onClick={close}
                    style={{
                        background: "#fff",
                        borderRadius: "50%",                                    
                        padding: 4 * sizeMultiplier,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}                        
                >                                     
                    <CloseIcon style={{ color: "#808080", fontSize: 24 * sizeMultiplier, width: 28 * sizeMultiplier, height: 28 * sizeMultiplier,}} />                        
                </IconButton>
            </div>
        );                    
    }

    function renderGoogleMapsAttribution(){
        const logoHeight = 16 * sizeMultiplier;

        return (
            <div
                translate="no"
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    paddingTop: 16 * sizeMultiplier,
                    paddingBottom: 16 * sizeMultiplier + 36 * sizeMultiplier + 16 * sizeMultiplier,
                    visibility: showTexts ? 'visible' : 'hidden',
                    position: 'relative',
                    zIndex: 30,
                    pointerEvents: 'none',
                }}
            >
                <img
                    alt="Google Maps"
                    src="/images/GoogleMaps_Logo_Gray_2x.png"
                    draggable={false}
                    style={{
                        height: logoHeight,
                        width: 'auto',
                        pointerEvents: 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                    }}
                />
            </div>
        );
    }
    
      let showExtendedHoursInfo = false;
      if (props.show){   
        const language = browserLocales(true)[0] === "fr" ? "fr" : "en";                              
        
        // with v2 the isopened is not set (the hours content is called from HoursButton)
        const rawWeekdayDescriptions = props.placeDetails?.currentOpeningHours?.weekdayDescriptions;
        const hasWeekdayDescriptions = Array.isArray(rawWeekdayDescriptions) && rawWeekdayDescriptions.length > 0;

        if (activeFeature === "hours" && props.placeDetails && hasWeekdayDescriptions){                   
            let hoursFontSize = 22;
            let weekDayOpenings = null;
            let secondaryWeekDayOpenings = "";                
            
            let weekdayDescriptions = rawWeekdayDescriptions;                
            if (language === "en" && !props.story.premium){
                weekdayDescriptions = translateWeekdayDescriptions(rawWeekdayDescriptions);                    
            }

            const weekDayOpeningsTable = weekdayTextToGroupedByDaysOpeningHours(weekdayDescriptions);
            if (!weekDayOpeningsTable?.format) {
                return null;
            }

            if (weekDayOpeningsTable.format === "day"){
                hoursFontSize = 18;                    
                weekDayOpenings = weekDayOpeningsTable.openingHours.map((weekDayOpening, index) =>
                <React.Fragment key={`opening-${index}-${weekDayOpening}`}>
                    <tr/><tr/>
                    <tr key={weekDayOpening}><td style={{textAlign: 'center'}}>{
                        cutMultipleHours(weekDayOpening)
                        }</td></tr>
                </React.Fragment>
                );
            }
            else if (weekDayOpeningsTable.format === "grouped"){                    
                hoursFontSize = 22;
                weekDayOpenings = weekDayOpeningsTable.openingHours.map((weekDayOpening, index) =>
                    {                                             
                        if (weekDayOpening.startingDay === weekDayOpening.endingDay){
                            return (<React.Fragment key={`grouped-opening-${index}-${weekDayOpening.startingDay}`}><tr key={weekDayOpening.startingDay}><td style={{textAlign: 'center'}}>{                               
                                weekDayOpening.startingDay} 
                            </td></tr>
                            <tr key={weekDayOpening.startingDay+"bis"}><td style={{textAlign: 'center', fontStyle: 'italic'}}>                               
                                <i>{cutMultipleHours(weekDayOpening.hours)}</i>                        
                                </td></tr><tr/><tr/>
                            <tr/><tr/></React.Fragment>);
                        }
                        else {
                            return (
                            <React.Fragment key={`grouped-opening-${index}-${weekDayOpening.startingDay}-${weekDayOpening.endingDay}`}>
                            <tr key={weekDayOpening.startingDay}><td style={{textAlign: 'center'}}>{                                
                                t('HoursFromTo', {from: weekDayOpening.startingDay, to: weekDayOpening.endingDay})                         
                                }</td></tr>
                            <tr key={weekDayOpening.startingDay+"bis"}><td style={{textAlign: 'center', fontStyle: 'italic'}}>{                                
                                cutMultipleHours(weekDayOpening.hours)                        
                                }</td></tr><tr/><tr/>
                            </React.Fragment>);
                        }                                                    
                    });
                
            }
            if (props.placeDetails.currentSecondaryOpeningHours){
                secondaryWeekDayOpenings = props.placeDetails.currentSecondaryOpeningHours.map((openingHours, secondaryIndex) => {
                    const secondaryTable = weekdayTextToGroupedByDaysOpeningHours(openingHours.weekdayDescriptions);
                    if (!secondaryTable?.openingHours?.length) {
                        return null;
                    }

                    const title = (
                        <React.Fragment key={`secondary-title-${secondaryIndex}`}>
                            <tr/><tr/><tr/><tr/><tr/><tr/><tr/><tr/><tr/><tr>
                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{t(openingHours.secondaryHoursType)}</td>
                            </tr>
                        </React.Fragment>
                    );

                    if (secondaryTable.format === "day") {
                        return (
                            <React.Fragment key={`secondary-day-${secondaryIndex}-${openingHours.secondaryHoursType}`}>
                                {title}
                                {secondaryTable.openingHours.map((weekDayOpening, index) =>
                                    <React.Fragment key={`secondary-day-line-${secondaryIndex}-${index}-${weekDayOpening}`}>
                                        <tr/><tr/>
                                        <tr><td style={{ textAlign: 'center' }}>{cutMultipleHours(weekDayOpening)}</td></tr>
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        );
                    }

                    const weekDayOpenings = secondaryTable.openingHours.map((weekDayOpening, index) => {
                        if (weekDayOpening.startingDay === weekDayOpening.endingDay) {
                            return (
                                <React.Fragment key={`secondary-opening-${secondaryIndex}-${index}-${weekDayOpening.startingDay}`}>
                                    <tr><td style={{ textAlign: 'center' }}>{weekDayOpening.startingDay}</td></tr>
                                    <tr><td style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                        <i>{cutMultipleHours(weekDayOpening.hours)}</i>
                                    </td></tr>
                                    <tr/><tr/><tr/><tr/>
                                </React.Fragment>
                            );
                        }
                        return (
                            <React.Fragment key={`secondary-opening-${secondaryIndex}-${index}-${weekDayOpening.startingDay}-${weekDayOpening.endingDay}`}>
                                <tr><td style={{ textAlign: 'center' }}>
                                    {t('HoursFromTo', { from: weekDayOpening.startingDay, to: weekDayOpening.endingDay })}
                                </td></tr>
                                <tr><td style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                    {cutMultipleHours(weekDayOpening.hours)}
                                </td></tr>
                                <tr/><tr/>
                            </React.Fragment>
                        );
                    });

                    return (
                        <React.Fragment key={`secondary-grouped-${secondaryIndex}-${openingHours.secondaryHoursType}`}>
                            {title}
                            {weekDayOpenings}
                        </React.Fragment>
                    );
                });
            }      
            
            const additionalStyle = {};
            let logoMarginTop = 32 * sizeMultiplier;
            if (weekDayOpeningsTable.format === "grouped" && !props.placeDetails.currentSecondaryOpeningHours){                    
                additionalStyle.display = "flex";
                additionalStyle.flexDirection = "column";
                additionalStyle.justifyContent = "center";
                additionalStyle.alignItems = "center";
                additionalStyle.alignContent = "center";     
                logoMarginTop = -72 * sizeMultiplier;               
            };

            const logo = props.story.hoursLogo ? props.story.hoursLogo : props.story.avatarUrl ? props.story.avatarUrl : "/images/avatar_placeholder.png";
            const logoStyle = {
                zIndex: 20
            };
            if (props.story.hoursLogo){                    
                logoStyle.maxWidth = storyDimensions.containerWidth - 100 * sizeMultiplier;
                logoStyle.maxHeight = 96 * sizeMultiplier;                    
            }
            else {
                logoStyle.width = 96 * sizeMultiplier;    
                logoStyle.height = 96 * sizeMultiplier;
                logoStyle.borderRadius = 50;
            }

            let textColor = props.story.primaryColor;

            const ccc = new ColorContrastChecker();

            if (!ccc.isLevelCustom(props.story.primaryColor, "#FFFFFF", 3)) {
                if (!ccc.isLevelCustom(props.story.secondaryColor, "#FFFFFF", 3)) {
                    textColor = "#000000";
                }
                else {                        
                    textColor = props.story.secondaryColor;
                }
            }                 

            return (
                <FadeInPanel
                    className="hours-container"
                    animationKey="hours-full"
                    style={hoursPanelRootStyle}
                    onClick={(e) => e.stopPropagation()}
                >
                    {renderHoursBackdrop()}
                    <div
                        className="hours-scrollable-container"
                        style={{ ...hoursScrollAreaStyle, ...additionalStyle }}
                    >
                        <div style={{marginTop: logoMarginTop, width: "100%", display: 'flex', flexDirection: 'row', justifyContent: 'center', zIndex: 20}}>
                            <img src={logo} style={{...logoStyle}} onLoad={()=>{setShowTexts(true);}} alt="" />
                        </div>
                        <div style={{marginTop: (16+2) * sizeMultiplier, fontSize: 36 * sizeMultiplier, color: textColor, fontWeight: 'bolder', position: "relative", zIndex: 20, width: "100%", textAlign: "center", visibility: showTexts ? 'visible' : 'hidden'}}>
                            {t('Hours')}
                        </div>
                        <div id="hours-content-container" style={{marginTop: 16, width: "100%", visibility: showTexts ? 'visible' : 'hidden'}}>
                            <table className="hours-openings" style={{position: "relative", fontSize: hoursFontSize * sizeMultiplier, color: textColor + "D0", width: storyDimensions.containerWidth - 60 * sizeMultiplier}}>
                                <tbody>
                                    {weekDayOpenings}
                                    {secondaryWeekDayOpenings}
                                    {showExtendedHoursInfo && <div style={{marginTop: 10 * sizeMultiplier, fontSize: 14 * sizeMultiplier, width: "100%", fontStyle: 'italic', textAlign: 'center'}}>{t("HoursInfoExtended")}</div>}
                                </tbody>
                            </table>
                        </div>
                        {renderGoogleMapsAttribution()}
                    </div>
                    {showCloseButton()}
                </FadeInPanel>
            );
        }
        else if (activeFeature === "hours" && props.placeDetails && !hasWeekdayDescriptions){                 
            let logoMarginTop = 32 * sizeMultiplier;

            const logo = props.story.hoursLogo ? props.story.hoursLogo : props.story.avatarUrl ? props.story.avatarUrl : "/images/avatar_placeholder.png";
            const logoStyle = {
                zIndex: 20
            };
            if (props.story.hoursLogo){                    
                logoStyle.maxWidth = storyDimensions.containerWidth - 100 * sizeMultiplier;
                logoStyle.maxHeight = 96 * sizeMultiplier;                    
            }
            else {
                logoStyle.width = 96 * sizeMultiplier;    
                logoStyle.height = 96 * sizeMultiplier;
                logoStyle.borderRadius = 50;
            }
            let textColor = props.story.primaryColor;

            const ccc = new ColorContrastChecker();

            if (!ccc.isLevelCustom(props.story.primaryColor, "#FFFFFF", 3)) {
                if (!ccc.isLevelCustom(props.story.secondaryColor, "#FFFFFF", 3)) {
                    textColor = "#000000";
                }
                else {                        
                    textColor = props.story.secondaryColor;
                }
            }    

            return (
                <FadeInPanel
                    className="hours-container"
                    animationKey="hours-empty"
                    style={hoursPanelRootStyle}
                    onClick={(e) => e.stopPropagation()}
                >
                    {renderHoursBackdrop()}
                    <div className="hours-scrollable-container" style={hoursScrollAreaStyle}>
                        <div style={{marginTop: logoMarginTop, width: "100%", display: 'flex', flexDirection: 'row', justifyContent: 'center', zIndex: 20}}>
                            <img src={logo} style={{...logoStyle}} onLoad={()=>{setShowTexts(true);}} alt="" />
                        </div>
                        <div style={{marginTop: (16+2) * sizeMultiplier, fontSize: 36 * sizeMultiplier, color: textColor, fontWeight: 'bolder', position: "relative", zIndex: 20, width: "100%", textAlign: "center", visibility: showTexts ? 'visible' : 'hidden'}}>
                            {t('Hours')}
                        </div>
                        {renderGoogleMapsAttribution()}
                    </div>
                    {showCloseButton()}
                </FadeInPanel>
            );
        }
        else {                            
            return null;            
        }
    }
    else {
        return null;
    }
}

export default HoursScreen;